import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ShareButton from '@/components/ShareButton';
import axios from 'axios';
import { Calendar, FileText, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CATEGORIES = ['Agriculture', 'Formation', 'Réseautage', 'Évaluation', 'Autre'];

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const formatSize = (n) => {
  if (!n) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchActivities = useCallback(async () => {
    try {
      const url = selectedCategory 
        ? `${API}/activities?category=${selectedCategory}`
        : `${API}/activities`;
      const response = await axios.get(url);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const categories = ['Agriculture', 'Formation', 'Réseautage', 'Évaluation', 'Autre'];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="activities-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="activities-title"
            >
              Nos Activités et Rapports
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Consultez les données et rapports de nos activités sur le terrain.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            <button
              onClick={() => setSelectedCategory(null)}
              data-testid="category-filter-all"
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory 
                  ? 'bg-[#4A6B48] text-white' 
                  : 'bg-white border border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48]'
              }`}
            >
              Toutes
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`category-filter-${cat.toLowerCase()}`}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat
                    ? 'bg-[#4A6B48] text-white' 
                    : 'bg-white border border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}
          {!loading && activities.length === 0 && (
            <div className="text-center py-12" data-testid="no-activities-message">
              <p className="text-[#5C665A]">Aucune activité disponible pour cette catégorie.</p>
            </div>
          )}
          {!loading && activities.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {activities.map((activity) => (
                <div key={activity.activity_id} className="card" data-testid={`activity-card-${activity.activity_id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-[#D87D4A] px-3 py-1 bg-[#D87D4A]/10 rounded-full">
                      {activity.category}
                    </span>
                    <div className="flex items-center space-x-2 text-[#5C665A] text-sm">
                      <Calendar size={16} />
                      <span>{new Date(activity.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  
                  <h3 
                    className="text-xl font-bold text-[#2E332D] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {activity.title}
                  </h3>
                  
                  <p className="text-sm text-[#5C665A] leading-relaxed mb-4">
                    {activity.description}
                  </p>

                  {activity.images && activity.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {activity.images.slice(0, 3).map((img, idx) => (
                        <img 
                          key={`${activity.activity_id}-img-${img}`}
                          src={img} 
                          alt={`${activity.title} ${idx + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                      ))}
                    </div>
                  )}

                  {(() => {
                    const atts = activity.attachments || [];
                    if (atts.length > 0) {
                      return (
                        <div className="border-t border-[#E5E3DB] pt-4 mt-4" data-testid={`activity-attachments-${activity.activity_id}`}>
                          <h4 className="text-sm font-bold text-[#4A6B48] mb-2 inline-flex items-center gap-1.5">
                            <FileText size={14} /> Documents joints
                          </h4>
                          <ul className="space-y-2">
                            {atts.map((att, idx) => (
                              <li key={`${activity.activity_id}-${att.url}-${idx}`}
                                className="flex items-center gap-2 p-2 bg-[#F9F8F6] rounded border border-[#E5E3DB]">
                                <FileText size={16} className="text-[#4A6B48] shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-[#2E332D] truncate">{att.title || att.filename}</p>
                                  {att.size > 0 && <span className="text-xs text-[#5C665A]">{formatSize(att.size)}</span>}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <ShareButton
                                    title={att.title || att.filename || activity.title}
                                    url={`${window.location.origin}/activites#${activity.activity_id}`}
                                    message={`Découvrez ce document de l'activité « ${activity.title} » :`}
                                    size="sm"
                                    variant="icon"
                                    testId={`share-attachment-${activity.activity_id}-${idx}`}
                                  />
                                  <button
                                    onClick={() => setPreviewDoc({
                                      title: att.title || att.filename || activity.title,
                                      url: resolveUrl(att.url),
                                    })}
                                    data-testid={`view-attachment-${activity.activity_id}-${idx}`}
                                    className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1.5 rounded border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white"
                                  >
                                    <Eye size={12} /> Consulter
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    if (activity.document_url) {
                      return (
                        <button
                          onClick={() => setPreviewDoc({
                            title: activity.title,
                            url: resolveUrl(activity.document_url),
                          })}
                          data-testid={`activity-document-${activity.activity_id}`}
                          className="inline-flex items-center gap-2 text-sm font-bold text-[#4A6B48] hover:underline mt-2"
                        >
                          <Eye size={14} />
                          Consulter le document
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* View-only PDF preview modal (no download) */}
      {previewDoc && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          data-testid="activity-attachment-preview"
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E3DB]">
              <h3 className="font-bold text-[#2E332D] truncate">{previewDoc.title}</h3>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-[#5C665A] italic hidden sm:inline">
                  Consultation uniquement
                </span>
                <button
                  onClick={() => setPreviewDoc(null)}
                  data-testid="close-activity-preview"
                  className="text-[#5C665A] hover:text-[#2E332D] font-bold text-sm px-2"
                >
                  Fermer
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#F9F8F6]" onContextMenu={(e) => e.preventDefault()}>
              <iframe
                title={previewDoc.title}
                src={`${previewDoc.url}#toolbar=0&navpanes=0&scrollbar=1&statusbar=0`}
                className="w-full h-full"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
